import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Heart, ArrowLeft, Pill, Calendar, Users, MessageCircle, Sun } from "lucide-react";

export default function CareMode() {
  const { chestId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/care-mode/${chestId}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Could not load"));
  }, [chestId]);

  if (error) return (
    <div className="min-h-screen bg-[#FCFAF8] flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h1 className="font-display text-3xl mb-3">Care Mode is a Premium feature</h1>
        <p className="text-[#7A736E] mb-6">{error}</p>
        <Button onClick={() => nav("/dashboard")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]">Back to dashboard</Button>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#FCFAF8] flex items-center justify-center text-[#7A736E]">Loading…</div>;

  const isOwnerOrTeam = !!user;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FCFAF8] via-[#F3EFEA] to-[#FCFAF8] text-[#3D3935]">
      {isOwnerOrTeam && (
        <div className="max-w-4xl mx-auto px-6 pt-6 flex justify-between items-center print:hidden">
          <Link to={`/chest/${chestId}`} className="inline-flex items-center gap-2 text-sm text-[#7A736E] hover:text-[#3D3935]" data-testid="care-mode-back">
            <ArrowLeft className="w-4 h-4" /> Back to caregiver view
          </Link>
          <span className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">Care Mode • Premium</span>
        </div>
      )}

      <header className="max-w-4xl mx-auto px-6 pt-10 pb-8 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#D8E2D8] mb-6">
          <Sun className="w-10 h-10 text-[#3A5A4A]" />
        </div>
        <p className="text-lg text-[#7A736E] uppercase tracking-[0.2em]">{data.date_label}</p>
        <h1 className="font-display text-5xl md:text-7xl font-semibold mt-4 tracking-tight">
          Good day, {data.chest_name}.
        </h1>
        <p className="text-xl text-[#7A736E] mt-6 max-w-2xl mx-auto leading-relaxed">
          Here's your day. We've got you.
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-20 space-y-10">

        {/* Today's appointments */}
        {data.today_appointments?.length > 0 && (
          <section className="hc-card p-8 md:p-10" data-testid="care-mode-appointments">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#E8DCC8] flex items-center justify-center flex-shrink-0">
                <Calendar className="w-7 h-7 text-[#3A5A4A]" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold">Today's appointments</h2>
            </div>
            <ul className="space-y-5">
              {data.today_appointments.map((a) => (
                <li key={a.id} className="text-xl md:text-2xl leading-relaxed">
                  <strong className="text-[#3A5A4A]">{a._when?.slice(11, 16) || "Today"}</strong>
                  {" — "}
                  <strong>{a.doctor}</strong>
                  {a.specialty && <span className="text-[#7A736E]"> ({a.specialty})</span>}
                  {a.location && <div className="text-base text-[#7A736E] mt-1 ml-12">📍 {a.location}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Medications */}
        {data.medications?.length > 0 && (
          <section className="hc-card p-8 md:p-10" data-testid="care-mode-medications">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#D8E2D8] flex items-center justify-center flex-shrink-0">
                <Pill className="w-7 h-7 text-[#3A5A4A]" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold">Your medications</h2>
            </div>
            <ul className="space-y-5">
              {data.medications.map((m) => (
                <li key={m.id} className="text-xl md:text-2xl leading-relaxed">
                  <strong>{m.name}</strong>
                  {m.dosage && <span className="text-[#7A736E]"> — {m.dosage}</span>}
                  {(m.time_of_day || m.frequency) && (
                    <div className="text-lg text-[#3A5A4A] mt-1">
                      {m.time_of_day || m.frequency}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-base text-[#7A736E] mt-6 italic">Always take medications as your doctor instructed.</p>
          </section>
        )}

        {/* Today's planner events */}
        {data.today_events?.length > 0 && (
          <section className="hc-card p-8 md:p-10" data-testid="care-mode-events">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#F3EFEA] flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-[#C87B67]" fill="#C87B67" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold">Today's plan</h2>
            </div>
            <ul className="space-y-4">
              {data.today_events.map((e) => (
                <li key={e.id} className="text-xl md:text-2xl leading-relaxed">
                  {e.time && <strong className="text-[#3A5A4A]">{e.time}</strong>}
                  {e.time && " — "}
                  {e.title}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Care team */}
        {data.care_team?.length > 0 && (
          <section className="hc-card p-8 md:p-10" data-testid="care-mode-team">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#D8E2D8] flex items-center justify-center flex-shrink-0">
                <Users className="w-7 h-7 text-[#3A5A4A]" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold">People who love you</h2>
            </div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {data.care_team.map((c) => (
                <li key={c.id} className="text-lg md:text-xl">
                  <strong>{c.name}</strong>
                  {c.relationship && <span className="text-[#7A736E]"> · {c.relationship}</span>}
                  {c.phone && <div className="text-base text-[#3A5A4A] mt-1">📞 {c.phone}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Today's care log */}
        {data.today_care_log?.length > 0 && (
          <section className="hc-card p-8 md:p-10" data-testid="care-mode-log">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#E8DCC8] flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-7 h-7 text-[#3A5A4A]" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold">Notes from the day</h2>
            </div>
            <ul className="space-y-5">
              {data.today_care_log.map((l) => (
                <li key={l.id} className="text-lg md:text-xl leading-relaxed">
                  "{l.entry}"
                  {l.mood && <div className="text-base text-[#7A736E] mt-1 italic">— feeling {l.mood}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Empty state if nothing scheduled */}
        {!data.today_appointments?.length && !data.today_events?.length && !data.medications?.length && (
          <section className="hc-card p-12 text-center" data-testid="care-mode-empty">
            <Sun className="w-12 h-12 text-[#D9A441] mx-auto mb-4" />
            <h2 className="font-display text-3xl mb-3">A gentle, open day.</h2>
            <p className="text-xl text-[#7A736E]">Nothing scheduled. Rest, breathe, enjoy.</p>
          </section>
        )}

        <p className="text-center text-base text-[#7A736E] pt-8 italic">
          Made with care by your Hope Chest family.
        </p>
      </main>
    </div>
  );
}
